import React, { useMemo } from 'react';
import { Button } from 'antd';
import moment from 'moment';
import { IBestDataset } from '../../../../../../types/analytics';
import { IEnrichedMetadata } from '../../../../../../types/metadata';
import { IAdvancedAnalyticsApiPayload } from '../../../../../../types/chart';
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput';
import { MarkerItem } from '../../../../../../types/home';
import { useExceedanceParams } from '../../../hooks/useExceedanceParams';
import { AnalyticsSection } from '../../Layout/AnalyticsSection';
import ExceedanceInputForm from './ExceedanceInputForm';
import ExceedanceChart from './ExceedanceChart';

interface ExceedanceProps {
  coordinates: number[];
  previewDatasetId: string;
  datasets: IBestDataset[];
  onDatasetChange: (item: string) => void;
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  exceedanceValuesImage: string;
  exceedanceValuesLinks: string[];
  error: string;
  updating: boolean;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem;
  onChangeActiveKey: (item: any) => void;
  pointOption: { label: string; value: string }[];
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  isOfflineDataset: boolean;
}

export default function Exceedance(props: ExceedanceProps) {
  const {
    datasets,
    exceedanceValuesImage,
    exceedanceValuesLinks,
    error,
    updating,
    pointName,
    isOfflineDataset,
  } = props;

  const {
    listVariablesX,
    dataset,
    variableX,
    startDate,
    endDate,
    categories,
    onUpdateExceedance,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  } = useExceedanceParams(props);

  // TODO: enable cache
  // const upsertExceedanceCache = useAnalyticsCacheStore(state => state.upsertExceedanceCache)

  // useEffect(() => {
  //   if (!props.previewDatasetId || !variableX?.Index) return;

  //   const key = `${props.previewDatasetId}_${variableX.Index}_${props.coordinates.join(',')}`;

  //   upsertExceedanceCache(key, {
  //     variableXIndex: variableX.Index,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     exceedanceValuesImage,
  //     exceedanceValuesLinks
  //   })
  // }, [upsertExceedanceCache, props.previewDatasetId, props.coordinates, variableX?.Index, startDate, endDate, props.isForAllAnalytics, exceedanceValuesImage, exceedanceValuesLinks]);

  const plotInputContent = useMemo(
    () => (
      <ExceedanceInputForm
        datasets={datasets}
        dataset={props.previewDatasetId}
        onSelectDataset={onSelectDataset}
        categories={categories}
        variables={listVariablesX}
        variableX={variableX}
        onVariableChange={doVariableChange}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={doStartDateChange}
        onEndDateChange={doEndDateChange}
        disabledStartDate={disabledStartDate}
        disabledEndDate={disabledEndDate}
        isForAllAnalytics={props.isForAllAnalytics}
        onCheckboxChange={handleCheckboxChange}
        isLoading={updating}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [
      datasets,
      dataset,
      onSelectDataset,
      categories,
      listVariablesX,
      variableX,
      doVariableChange,
      startDate,
      endDate,
      doStartDateChange,
      doEndDateChange,
      disabledStartDate,
      disabledEndDate,
      props.isForAllAnalytics,
      handleCheckboxChange,
      updating,
      error,
    ]
  );

  const updateContent = useMemo(
    () => (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '36px',
        }}
      >
        <Button
          type='primary'
          id='Analytics-Exceedance-Update_Button'
          disabled={updating || isOfflineDataset}
          loading={updating}
          onClick={() => onUpdateExceedance()}
          style={{
            width: '90px',
            height: '36px',
          }}
        >
          Update
        </Button>
      </div>
    ),
    [updating, onUpdateExceedance]
  );

  const chartContent = useMemo(
    () => (
      <ExceedanceChart
        exceedanceValuesImage={exceedanceValuesImage}
        exceedanceValuesLinks={exceedanceValuesLinks}
        pointName={pointName}
        isLoading={updating}
        error={error}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [exceedanceValuesImage, exceedanceValuesLinks, pointName, updating, error]
  );

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={null}
      isLoading={updating}
    />
  );
}
